import React, { useEffect, useState, useRef } from "react"; 
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
// If you want extra safety for markdown, install and enable:
// import rehypeSanitize from "rehype-sanitize";
import { jsPDF } from "jspdf";          
import html2canvas from "html2canvas";  

function GeminiChat() {
  const navigate = useNavigate();
  // Add userData state for header logic
  const [userData, setUserData] = useState({ scores: [] });

  // --- Chat state ---
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [demographics, setDemographics] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [history, setHistory] = useState([]);

  // --- Report generator state ---
  const [reportType, setReportType] = useState("short_term");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportOutput, setReportOutput] = useState("");

  const reportRef = useRef(null); // wrap the rendered report for PDF

  const parseJsonSafe = async (res) => {
    const contentType = res.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    const bodyText = await res.text();
    if (isJson) {
      try {
        return JSON.parse(bodyText);
      } catch (e) {
        throw new Error(
          `Invalid JSON response: ${e.message}. Body: ${bodyText.slice(0, 200)}`
        );
      }
    }
    throw new Error(`Non-JSON response (${res.status}): ${bodyText.slice(0, 200)}`);
  };

  useEffect(() => {
    const loadDemographics = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_FLASK_API_URL;
        if (!baseUrl) throw new Error("REACT_APP_BACKEND_FLASK_API_URL not set");
        const res = await fetch(`${baseUrl}/llm/demographics-confirmation`, {
          method: "GET",
          credentials: "include",
        });
        const data = await parseJsonSafe(res);
        if (!res.ok) throw new Error(data.error || "Failed to load demographics");
        setDemographics(data.demographics || {});
        setRecommendations(data.recommendations || []);
        setConfirmation(data.confirmation || "");
      } catch (e) {
        setError(e.message);
      }
    };
    loadDemographics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch userData for header logic
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_FLASK_API_URL;
        if (!baseUrl) return;
        const res = await fetch(`${baseUrl}/profile`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        setUserData(data || { scores: [] });
      } catch (e) {
        setUserData({ scores: [] });
      }
    };
    fetchUserData();
  }, []);

  const sendMessage = async () => {
    setLoading(true);
    setError("");
    setReply("");
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_FLASK_API_URL;
      if (!baseUrl) throw new Error("REACT_APP_BACKEND_FLASK_API_URL not set");
      const newHistory = [...history, { role: "user", text: message }];
      const res = await fetch(`${baseUrl}/llm/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message,
          history, // keep as-is; backend appends current message separately
          demographics,
          recommendations,
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || "Request failed");
      setReply(data.text || "(No response)");
      setHistory([...newHistory, { role: "gemini", text: data.text || "" }]);
      setMessage("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Report generator: call /llm/report ---
  const generateReport = async () => {
    setReportLoading(true);
    setReportError("");
    setReportOutput("");
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_FLASK_API_URL;
      if (!baseUrl) throw new Error("REACT_APP_BACKEND_FLASK_API_URL not set");

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);

      const res = await fetch(`${baseUrl}/llm/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ report_type: reportType }),
        signal: controller.signal,
      });

      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.error || "Report request failed");
      setReportOutput(data.text || "(No response)");
      clearTimeout(timeout);
    } catch (e) {
      setReportError(e.name === "AbortError" ? "Report generation timed out" : e.message);
    } finally {
      setReportLoading(false);
    }
  };

  const clearReport = () => {
    setReportOutput("");
    setReportError("");
  };

  // --- Download the visible report as an A4 PDF (multi-page if needed)
  const downloadReportPdf = async () => {
    try {
      if (!reportOutput || !reportRef.current) {
        setReportError("Please generate a report first.");
        return;
      }

      // Render the report DOM to a high-res canvas
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // mm
      const usableWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width; // mm scale is automatic

      // Add first page
      let heightLeft = imgHeight;
      let position = margin;
      pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
      heightLeft -= (pageHeight - margin * 2);

      // Add extra pages if content overflows
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - (imgHeight - heightLeft);
        pdf.addImage(imgData, "PNG", margin, position, usableWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2);
      }

      const filename = `empathy-${reportType}-report.pdf`;
      pdf.save(filename);
    } catch (err) {
      setReportError(`PDF failed: ${err.message}`);
    }
  };

  // Add handleLogout for header
  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div style={{ padding: "2.5rem 0.5rem", maxWidth: 820, margin: "0 auto", textAlign: "left" }}>
      <header className="dash-header">
        <button onClick={() => navigate("/dashboard")}>Dashboard</button>
        <button
          onClick={() => {
            if (userData.scores && userData.scores.length === 0) {
              navigate("/consent", { state: { from: "dashboard" } });
            } else {
              navigate("/quiz-nav");
            }
          }}
        >
          Quiz
        </button>
        <button onClick={handleLogout}>Log Out</button>
      </header>

      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 2px 12px #0001", padding: 32, marginBottom: 32 }}>
        <h2 style={{ marginTop: 0 }}>Gemini LLM</h2>
        <p style={{ color: "#444", marginBottom: 24 }}>Ask a question related to your results or empathy development.</p>

        {demographics && (
          <div style={{ marginBottom: 18, background: "#fafafa", padding: 16, border: "1px solid #eee", borderRadius: 8 }}>
            <strong>Your demographics (from profile)</strong>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 14 }}>
{JSON.stringify(demographics, null, 2)}
            </pre>
            {recommendations && recommendations.length > 0 && (
              <>
                <strong>Your recommendations</strong>
                <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 14 }}>
{JSON.stringify(recommendations, null, 2)}
                </pre>
              </>
            )}
            {confirmation && (
              <div style={{ marginTop: 10 }}>
                <em>Gemini confirmation:</em>
                <div style={{ background: "#f6f8fa", border: "1px solid #e0e0e0", borderRadius: 6, padding: 10, marginTop: 4 }}>
                  <ReactMarkdown>{confirmation}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat input */}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your question..."
          style={{ width: "100%", height: 120, padding: 14, fontSize: 16, borderRadius: 8, border: "1px solid #ddd", marginBottom: 8 }}
        />
        <div style={{ marginTop: 10, display: "flex", gap: 10 }}>
          <button
            onClick={sendMessage}
            disabled={loading || !message.trim()}
            style={{ background: "#1976d2", color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", fontWeight: 600, fontSize: 16, boxShadow: loading ? "none" : "0 1px 4px #1976d220", opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "Sending..." : "Send"}
          </button>
        </div>

        {error && <div style={{ color: "#b00020", marginTop: 12, fontWeight: 500 }}>Error: {error}</div>}

        {reply && (
          <div style={{ marginTop: 24, background: "#f6f8fa", border: "1px solid #e0e0e0", borderRadius: 8, padding: 18 }}>
            <h3 style={{ marginTop: 0 }}>Gemini Reply</h3>
            <ReactMarkdown>{reply}</ReactMarkdown>
          </div>
        )}

        {/* Conversation history */}
        <div style={{ marginTop: 28 }}>
          <h3>Conversation History</h3>
          <div style={{ background: "#f5f5f5", padding: 14, borderRadius: 8, fontSize: 15 }}>
            {history.map((turn, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <b>{turn.role === "user" ? "You" : "Gemini"}:</b> {turn.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Report Generator */}
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 2px 12px #0001", padding: 32, marginBottom: 32 }}>
        <h2 style={{ marginTop: 0 }}>Generate Empathy Report</h2>
        <p style={{ color: "#444", marginBottom: 18 }}>Select a report type and click Generate. It uses your saved demographics and recommendations.</p>

        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <label style={{ fontWeight: 500 }}>
            Report type:&nbsp;
            <select value={reportType} onChange={(e) => setReportType(e.target.value)} style={{ borderRadius: 6, padding: "6px 10px", border: "1px solid #ccc" }}>
              <option value="short_term">Short-term recommendation</option>
              <option value="long_term">Long-term recommendation</option>
              <option value="action_plan">Action plan</option>
              <option value="summary">Summary report</option>
            </select>
          </label>

          <button onClick={generateReport} disabled={reportLoading} style={{ background: "#1976d2", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, fontSize: 15, boxShadow: reportLoading ? "none" : "0 1px 4px #1976d220", opacity: reportLoading ? 0.7 : 1 }}>
            {reportLoading ? "Generating..." : "Generate"}
          </button>

          <button onClick={clearReport} disabled={reportLoading && !reportOutput} style={{ background: "#f5f5f5", color: "#333", border: "1px solid #ccc", borderRadius: 6, padding: "8px 18px", fontWeight: 500 }}>
            Clear
          </button>

          <button onClick={downloadReportPdf} disabled={!reportOutput || reportLoading} style={{ background: "#43a047", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, fontSize: 15, opacity: !reportOutput || reportLoading ? 0.7 : 1 }}>
            Download PDF
          </button>
        </div>

        {reportError && <div style={{ color: "#b00020", marginTop: 12, fontWeight: 500 }}>Error: {reportError}</div>}

        {reportOutput && (
          <div style={{ marginTop: 20 }}>
            <h3>Report</h3>
            <div
              ref={reportRef}
              style={{
                background: "#fff",
                padding: 18,
                border: "1px solid #eee",
                borderRadius: 8,
                fontSize: 15,
              }}
            >
              <ReactMarkdown>{reportOutput}</ReactMarkdown>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
              <em>If the PDF cuts off, it will add additional pages automatically.</em>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GeminiChat;
